import {
  Box,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import times from "lodash/times";
import { DateTime } from "luxon";
import {
  getConversationTitle,
  useCoachBoConversations,
  useNavigate,
  useOpenConversation,
} from "../../../../modules/common";
import { MediumCustomIcon } from "../../../../modules/components/layer00";
import { HistoricalConversationListItem } from "../../../../modules/components/layer06";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectIsRecentChatsNavOpen,
  setIsRecentChatsNavOpen,
} from "app/containers/Chatbot/slice";
import { ChatbotConversation } from "app/containers/Chatbot/types";

export const RecentChatsNav = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const openConversation = useOpenConversation();
  const { conversations, status: conversationsStatus } =
    useCoachBoConversations();

  const open = useAppSelector(selectIsRecentChatsNavOpen);

  const sortedConversations = [...conversations].sort((a, b) => {
    const aTime =
      DateTime.fromISO((a.created_at ?? "").replace(" ", "T")).toMillis() || 0;
    const bTime =
      DateTime.fromISO((b.created_at ?? "").replace(" ", "T")).toMillis() || 0;
    return bTime - aTime;
  });

  const getListItemTitle = (conv: ChatbotConversation) =>
    getConversationTitle(conv) ??
    conv.messages?.at(-1)?.content ??
    "Conversation";

  const isLoading = conversationsStatus === "loading";

  return (
    <Box paddingTop="1.5rem" paddingBottom="1.5rem">
      <Stack
        direction="row"
        alignItems="center"
        columnGap=".5rem"
        paddingLeft="1.75rem"
        paddingRight="1.5rem"
      >
        <Typography variant="navBarGroupTitle" flex="1 1 0" minWidth={0} noWrap>
          Recent Chats
        </Typography>
        <IconButton
          aria-label="Search conversation history"
          // Search lives in Bo's Brain, which covers the full conversation history.
          onClick={() => navigate("/user/boBrain", { focusSearch: "true" })}
          sx={{ border: "none", flexShrink: 0 }}
        >
          <MediumCustomIcon
            icon="SEARCH"
            stroke={isDark ? "var(--team-app-stripe-mid)" : undefined}
          />
        </IconButton>
        <IconButton
          onClick={() => dispatch(setIsRecentChatsNavOpen(!open))}
          sx={{
            border: "none",
            transform: `rotate(${open ? "0deg" : "180deg"})`,
          }}
        >
          <MediumCustomIcon
            icon="CHEVRONUP"
            stroke={isDark ? "var(--team-app-stripe-mid)" : undefined}
          />
        </IconButton>
      </Stack>

      <Collapse in={open}>
        <Stack
          direction="column"
          paddingTop="1rem"
          paddingLeft="1.75rem"
          paddingRight="1.5rem"
          rowGap=".75rem"
          sx={{
            // Cap the list height so only the conversations scroll, not the whole nav.
            maxHeight: "20rem",
            overflowY: "auto",
          }}
        >
          {isLoading &&
            times(3, (i) => (
              <Stack key={i} rowGap=".125rem">
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="90%" height={16} />
              </Stack>
            ))}
          {!isLoading &&
            sortedConversations.map((conv, i) => (
              <HistoricalConversationListItem
                key={conv.threadId ?? i}
                content={getListItemTitle(conv)}
                createdAt={conv.created_at}
                isCheckIn={!!conv.weeklyCheckInLink}
                onClick={() => openConversation(conv)}
              />
            ))}
          {!isLoading && sortedConversations.length === 0 && (
            <Typography variant="chatMessageBody" color="text.secondary">
              No conversations yet.
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};
